/**
 * Shared exchange-identity helpers.
 *
 * DEX legs arrive from the analyzer with the chain short as the
 * exchange name (``eth`` / ``bsc`` / ``sol`` …) — sourced from
 * ``CHAIN_INDEX_TO_SHORT`` in ``src/chain_registry.py`` (45 entries
 * as of 2026-05-20). Anything outside this set plus the ``*-dex``
 * suffix pattern is treated as a CEX.
 *
 * Two failure modes if this list drifts from the backend registry:
 *   * Missing chain → it shows up in the FilterBar's ``Exchanges``
 *     dropdown as a fake CEX (operator complaint 2026-05-20: BOB,
 *     BTR, CHZ, ETHW, CFXEVM, IOTX, MANTA, OPBNB, IMX appeared in
 *     the CEX list because they weren't in ``DEX_CHAINS``).
 *   * Extra chain (not in backend) → harmless dead entry; ``isDexExchange``
 *     returns true for a string that never appears in any orderbook,
 *     so no UI surface is affected.
 *
 * Lives in ``lib/`` so it can be imported from both the Spreads
 * pipeline and individual row components without creating an import
 * cycle (the previous copy in ``SignalRow.tsx`` existed specifically
 * because of that cycle).
 */

export const DEX_CHAINS: ReadonlySet<string> = new Set([
  // ---- Canonical chain shorts (mirror src/chain_registry.py) ----
  // EVM L1s + L2s
  "eth",
  "bsc",
  "polygon",
  "arb",
  "op",
  "base",
  "avax",
  "fantom",
  "cronos",
  "linea",
  "scroll",
  "blast",
  "zksync",
  "mantle",
  "manta",
  "mode",
  "ape",
  "b2",
  "bob",
  "btr",         // Bitlayer
  "cfxevm",      // Conflux EVM
  "chz",         // Chiliz chain
  "ethw",        // Ethereum PoW
  "gno",         // Gnosis chain
  "imx",         // ImmutableX
  "iotx",        // IoTeX
  "merl",        // Merlin
  "monad",
  "opbnb",
  "pls",         // PulseChain
  "plasma",
  "pzkevm",      // Polygon zkEVM
  "sei",
  "sonic",
  "stable",
  "taiko",
  "unichain",
  "xlayer",
  "zeta",
  // Non-EVM
  "sol",
  "tron",
  "ton",
  "sui",
  "starknet",
  "aptos",
  // ---- Legacy long-name aliases (kept for backward compat with
  //      older orderbooks / signals that may use spelled-out forms) ----
  "solana",
  "ethereum",
  "arbitrum",
  "optimism",
  "avalanche",
  "polygon-pos",
  "matic",
  "pulsechain",
  "x-layer",
  "near",        // not currently indexed but used to be — keep
  "wormhole",    // bridge marker
]);

export function isDexExchange(ex: string): boolean {
  return DEX_CHAINS.has(ex) || ex.endsWith("-dex");
}
